import axios from 'axios';
import { postJobApiCall } from '../../utils/jobRequest';
import {
  VALIDATE_DYNAMIC_VOICE_CODE_FAILURE,
  VALIDATE_DYNAMIC_VOICE_CODE_REQUEST,
  VALIDATE_DYNAMIC_VOICE_CODE_SUCCESS,
  GET_TENANT_BRANDING,
} from '../Types/types';

// fetch tenant branding using the candidate-facing token from the invite link
export const fetchTenantBranding = (token) => async (dispatch) => {
  try {
    const baseUrl = (process.env.REACT_APP_AI_API_KEY || '').replace('/private/', '/');
    const response = await axios.get(`${baseUrl}tenant-branding/`, {
      params: { t: token },
      headers: {
        'x-api-key': process.env.REACT_APP_CANDIDATE_CHAT_API_KEY || '',
      },
    });
    if (response?.status === 200) {
      dispatch({ type: GET_TENANT_BRANDING, payload: response.data.data });
      return { success: true, data: response.data.data };
    }
    return { success: false };
  } catch (error) {
    console.error('Error fetching tenant branding:', error);
    return { success: false };
  }
};

// Re-sends the interview invite email from the "Not ready right now?" screen.
// payload is either
//   { call_type: "DYNAMIC_VOICE_CALL", voice_call_session_id, verification_code }
// or
//   { call_type: "WEB_CALL", candidate_hiring_stage_id }
export const resendInterviewInvite = (payload) => async () => {
  try {
    const response = await axios.post(
      `${process.env.REACT_APP_AI_API_KEY}job-candidate-hiring-stages/resend-invite`,
      payload,
      {
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': process.env.REACT_APP_CANDIDATE_CHAT_API_KEY || '',
        },
      }
    );
    const body = response?.data || {};
    return {
      success: Boolean(body.success && body.data?.sent !== false),
      status: response?.status,
      detail: '',
      message: body.message || '',
      data: body.data || null,
    };
  } catch (error) {
    // Errors aren't wrapped in success/data: `detail` is a string, except for
    // request-validation 400s where it's a list and `message` holds the text.
    const status = error?.response?.status ?? 0;
    const body = error?.response?.data || {};
    const isValidationError = Array.isArray(body.detail);
    const detail = isValidationError
      ? ''
      : typeof body.detail === 'string'
        ? body.detail
        : body.detail?.message || '';
    if (isValidationError || status === 401) {
      console.error('resend-invite request rejected', status, body);
    }
    return {
      success: false,
      status,
      detail,
      isValidationError,
      message: isValidationError ? body.message || '' : detail,
      data: null,
    };
  }
};

// Puts the candidate into the dynamic voice call slot queue. The slot itself is
// granted asynchronously through the queue websocket ("queue_item_ready"), and
// only then is the code validated to get the LiveKit connection details.
export const queueDynamicVoiceCall = (voice_call_session_id, verification_code) => async () => {
  const errorMessage = (body, status) => {
    const detail = body?.detail;
    if (typeof detail === 'string') return detail;
    if (detail?.message) return detail.message;
    if (body?.message) return body.message;
    return status === 403
      ? 'Insufficient credits. Please contact your administrator.'
      : 'Unable to join the interview queue. Please try again.';
  };

  try {
    const res = await postJobApiCall('dynamic-voice-call/queue', {
      voice_call_session_id,
      verification_code,
    });
    // postJobApiCall resolves with the axios error (instead of throwing) for
    // most failure statuses, so a missing 2xx status means it failed.
    if (res?.status >= 200 && res?.status < 300) {
      return { success: true, data: res.data?.data ?? res.data ?? {} };
    }
    return { success: false, message: errorMessage(res?.response?.data, res?.response?.status) };
  } catch (error) {
    return {
      success: false,
      message: errorMessage(error?.response?.data, error?.response?.status),
    };
  }
};

export const validateDynamicVoiceCode =(voice_call_session_id, verification_code) => async (dispatch) => {
  dispatch({ type: VALIDATE_DYNAMIC_VOICE_CODE_REQUEST });
  try {
    const res = await postJobApiCall("dynamic-voice-call/validate-code", {
      voice_call_session_id,
      verification_code,
    });
    dispatch({ type: VALIDATE_DYNAMIC_VOICE_CODE_SUCCESS, payload: res.data.data });
    return { success: true };
  } catch (error) {
    dispatch({ type: VALIDATE_DYNAMIC_VOICE_CODE_FAILURE });
    const message =
      error?.response?.data?.detail ||
      error?.response?.data?.message ||
      (error?.response?.status === 403
        ? "Insufficient credits. Please contact your administrator."
        : "Invalid verification code or Insufficient credits. Please contact your administrator.");

    return { success: false, message };
  }
};
